import dayjs from 'dayjs'
import gql from 'graphql-tag'
import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import set from 'lodash/set'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import toastr from 'toastr'
import { AutoForm } from 'uniforms-unstyled'
import { useMutation } from '../../../api/graphql'
import EventSeries from '../../../api/events/EventSeries'
import { flyerCandidateToFormChanges, flyerImageChanges, flyerMarks, flyerMarksAfterChange } from '../../../api/events/flyerPrefill'
import type { FlyerCandidate, FlyerImage, FlyerMarks, FormChange } from '../../../api/events/flyerPrefill'
import { eventsSummary, initRepeatDays, occurrencesInputFromModel, prepareToSubmit } from '../../../api/events/helpers'
import type { OccurrencesPreviewInput } from '../../../api/events/helpers'
import { eventFragment } from '../../../graphql/fragments'
import useCrossZoneConfirm from '../../../hooks/useCrossZoneConfirm'
import useFreshBuildSubmit from '../../../hooks/useFreshBuildSubmit'
import useOnImageUploadStateChange from '../../../hooks/useOnImageUploadStateChange'
import useRecordSubmissionMutation from '../../../hooks/useRecordSubmissionMutation'
import useServerOccurrencesPreview, { occurrencesPreviewKey } from '../../../hooks/useServerOccurrencesPreview'
import useUserOrganizations, { UserOrganizationsQuery } from '../../../hooks/useUserOrganizations'
import { Event } from '../../../models'
import { CurrentUserContext } from '../../../providers/CurrentUserContext'
import { EventFormSchema } from '../../../schemas/events/eventSchema'
import Routes from '../../../startup/routes'
import { RepeatingPatterns } from '../../../util/constants'
import { applyTempPlaceChange } from '../../../util/crossZoneConfirm'
import { formatPhoneForForm } from '../../../util/string'
import UniformsButton from '../form-fields/uniform-fields/UniformsButton'
import Loading from '../misc/Loading'
import CrossZoneConfirmModal from './CrossZoneConfirmModal'
import EventFields from './EventFields'
import { FlyerPrefillContext } from './FlyerPrefillContext'
import FlyerStartPanel from './FlyerStartPanel'
import { OrganizerType } from './OrganizerTypeSelector'
import { ResolvedZoneContext } from './ResolvedZoneContext'
import SocialShareModal from './SocialShareModal'

const EVENT_NEW = gql`
  mutation EventCreate($input: EventCreateInput!) {
    eventCreate(input: $input) {
      event {
        ...eventFragment
      }
      errors {
        attribute
        message
      }
    }
  }
  ${eventFragment}
`

const SAVE_DEFAULT_ORGANIZER = gql`
  mutation SaveDefaultOrganizer($input: ProfileUpdateInput!) {
    profileUpdate(input: $input) {
      profile {
        id
        defaultOrganization {
          id
        }
      }
      errors {
        attribute
        message
      }
    }
  }
`

type Props = { event: Event; loading: boolean }
const EventNew: React.FC<Props> = ({ event: originalEvent, loading }) => {
  const history = useHistory()
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const formRef = useRef(null)

  const { currentUser } = useContext(CurrentUserContext)
  const { organizations, defaultOrganizationId, loading: orgsLoading } = useUserOrganizations()
  const [organizerName, setOrganizerName] = useState()
  const [organizerEmail, setOrganizerEmail] = useState()
  const [socialShareEvent, setSocialShareEvent] = useState<{ id: string; title: string; imagePublicId?: string; orgId?: string } | null>(null)
  const [saveAsDefault, setSaveAsDefault] = useState(false)
  const [updateDefaultOrganizer] = useMutation(SAVE_DEFAULT_ORGANIZER, {
    refetchQueries: [{ query: UserOrganizationsQuery }],
  })

  const onSuccess = useCallback(
    (record) => {
      const orgId = record?.submittedOnBehalfOfOrg?.id
      if (saveAsDefault && orgId && orgId !== defaultOrganizationId) {
        // Fire-and-forget: the event already submitted, so we don't surface failure here.
        updateDefaultOrganizer({ variables: { input: { firstName: currentUser?.firstName, defaultOrganizationId: orgId } } })
          .then(() => toastr.success('Saved as your default organizer'))
          .catch(() => undefined)
      }

      // Check if the event was submitted on behalf of an org that might have social accounts
      // Only show social share modal for admin users (feature gated for now)
      if (orgId && currentUser?.isAdmin) {
        // Try mutation response first (via fragment alias), fall back to form model
        const imagePublicId = record.image_publicId || record.imagePublicId ||
          get(formRef.current?.getModel(), 'image.publicId')
        setSocialShareEvent({
          id: record.id,
          title: record.title,
          imagePublicId: imagePublicId || undefined,
          orgId,
        })
        return
      }

      // No org or non-admin — standard redirect
      history.push(Routes.yourEvents())
      toastr.success(`
          Thank you!<br/><br/>
          Your event has been submitted.<br/>
        `)
    },
    [history, currentUser, saveAsDefault, defaultOrganizationId, updateDefaultOrganizer],
  )

  const handleSocialShareClose = useCallback(() => {
    setSocialShareEvent(null)
    history.push(Routes.yourEvents())
    toastr.success(`
        Thank you!<br/><br/>
        Your event has been submitted.<br/>
      `)
  }, [history])

  // The flyer read this event was started from (CVL-173), linked to the event when it saves.
  const flyerExtractionId = useRef<string | null>(null)
  const beforeSubmit = useCallback((doc) => {
    const input = prepareToSubmit(doc)
    return flyerExtractionId.current ? { ...input, flyerExtractionId: flyerExtractionId.current } : input
  }, [])

  const { handleSubmit, mutationLoading, onValidate } = useRecordSubmissionMutation({
    query: EVENT_NEW,
    queryName: 'eventCreate',
    recordName: 'event',
    beforeSubmit,
    onSuccess,
  })

  // Phase 10 (design rev.5 §5) — cross-zone explicit save confirmation. Track
  // the venue/community selection so the shared hook can fetch the
  // server-resolved zone (Phase-7 resolvedTimeZone == what EventCreate stores)
  // ONLY once one is actually picked (no-selection footgun handled in the
  // hook). Seeded from the initial model so a pre-filled primaryCommunity
  // (e.g. the user's default) is considered.
  const [placeId, setPlaceId] = useState<string | null>((originalEvent as { placeId?: string }).placeId ?? null)
  const [primaryCommunityId, setPrimaryCommunityId] = useState<string | null>(
    (originalEvent as { primaryCommunityId?: string }).primaryCommunityId ?? null,
  )
  // #898 item 1: the typed (not-in-directory) temp-venue state/city, so the
  // cross-zone confirm resolves a typed venue's zone (not the community's) — ==
  // what EventCreate stores. Seeded from the model's nested tempPlace.address.
  const seededTempAddress = (originalEvent as { tempPlace?: { address?: { state?: string; city?: string } } }).tempPlace
    ?.address
  const [tempPlaceState, setTempPlaceState] = useState<string | null>(seededTempAddress?.state ?? null)
  const [tempPlaceCity, setTempPlaceCity] = useState<string | null>(seededTempAddress?.city ?? null)
  // Only the venue saving will use (prepareToSubmit): the picked venue, or the typed-in one once
  // "not in directory" is ticked, never both.
  const [useTempPlace, setUseTempPlace] = useState<boolean>(Boolean((originalEvent as { useTempPlace?: boolean }).useTempPlace))
  const { guardSubmit, modalProps, resolvedZone, checkingZone } = useCrossZoneConfirm({
    mode: 'create',
    placeId: useTempPlace ? null : placeId,
    primaryCommunityId,
    tempPlaceAddressState: useTempPlace ? tempPlaceState : null,
    tempPlaceAddressCity: useTempPlace ? tempPlaceCity : null,
  })
  // Reload a stale tab instead of letting it submit the pre-redesign format
  // (which bakes the wall-clock in the browser's zone, not the venue's).
  const withFreshBuild = useFreshBuildSubmit()
  // A double click on Submit sends the event once: the second click can land before the save
  // shows as loading, so the save is marked as started until it finishes.
  const saving = useRef(false)
  // Once a save has started, a flyer read that finishes is ignored (a Submit stopped by a
  // missing field does not count).
  const saveStarted = useRef(false)
  const [flyerStopped, setFlyerStopped] = useState(false)
  const saveOnce = useCallback(
    (doc: unknown) => {
      if (saving.current) return
      saving.current = true
      saveStarted.current = true
      setFlyerStopped(true)
      handleSubmit(doc).finally(() => {
        saving.current = false
      })
    },
    [handleSubmit],
  )
  const onSubmit = useCallback(
    (doc: unknown) => withFreshBuild(() => guardSubmit(saveOnce)(doc)),
    [withFreshBuild, guardSubmit, saveOnce],
  )

  // Phase 11: the server-fed occurrences preview is seeded from, and updated
  // with, the form model.
  const seededInput = occurrencesInputFromModel(originalEvent)
  const [occurrencesInput, setOccurrencesInput] = useState<OccurrencesPreviewInput>({
    ...seededInput,
    placeId,
    primaryCommunityId,
  })
  const { occurrences: serverOccurrences } = useServerOccurrencesPreview(occurrencesInput)
  // Feed the server-expanded occurrences into the form model so the existing
  // read-only EventOccurrences preview renders them (unchanged consumer; only
  // the data SOURCE moved from the browser to the server). prepareToSubmit
  // still strips `occurrences` from the payload — this is preview-only.
  useEffect(() => {
    formRef.current?.change('occurrences', serverOccurrences)
  }, [serverOccurrences])

  const onImageUploadStateChange = useOnImageUploadStateChange(setIsSubmitDisabled)

  const updateSubmitterOwnsEvent = useCallback(
    (key, value) => {
      if (key !== 'submitterOwnsEvent') {
        return
      }
      const formReference = formRef.current
      if (!formReference) {
        return
      }

      if (value) {
        const model = formReference.getModel()
        setOrganizerName(get(model, 'organizer.name'))
        setOrganizerEmail(get(model, 'organizer.email'))
        formReference.change('organizer.name', get(currentUser, 'fullName'))
        formReference.change('organizer.email', get(currentUser, 'email'))
      } else {
        formReference.change('organizer.name', organizerName)
        formReference.change('organizer.email', organizerEmail)
      }
    },
    [currentUser, organizerEmail, organizerName, formRef, setOrganizerName, setOrganizerEmail],
  )

  const setRepeatStopsOnBasedOnRepeatingPattern = useCallback((formReference, key: string, value): boolean | undefined => {
    if (key !== 'repeats') {
      return
    }

    const model = formReference.getModel() as Event
    const repeatDays = initRepeatDays(value, model.startAt)
    if (repeatDays) {
      formReference.change('repeatDays', repeatDays)
    }

    let repeatStopsOn
    switch (value) {
      case RepeatingPatterns.DAILY:
      case RepeatingPatterns.REQUEST_SCHEDULE:
        repeatStopsOn = dayjs(model.startAt).add(5, 'day').toDate()
        formReference.change('repeatStopsOn', repeatStopsOn)
        return true
      case RepeatingPatterns.WEEKLY:
        repeatStopsOn = dayjs(model.startAt).add(4, 'week').toDate()
        formReference.change('repeatStopsOn', repeatStopsOn)
        return true
      case RepeatingPatterns.MONTHLY_NTH_DOW:
        const eventSeries = new EventSeries({ startAt: model.startAt, repeats: value })
        let nextOccurrence = eventSeries.next(model.startAt)
        nextOccurrence = eventSeries.next(nextOccurrence)
        repeatStopsOn = nextOccurrence
        formReference.change('repeatStopsOn', repeatStopsOn)
        return true
      case RepeatingPatterns.MONTHLY_SAME_DATE:
        repeatStopsOn = dayjs(model.startAt).add(2, 'month').toDate()
        formReference.change('repeatStopsOn', repeatStopsOn)
        return true
      default:
        break
    }
  }, [])

  // PHASE 11 (design rev.5 Open-risks option (a); plan Phase 11): the client
  // occurrence computation (the old `eventDates`/weekly loop that wrote
  // `model.occurrences`) was REMOVED. As of Phase 8a the SERVER expands the
  // series in the resolved venue zone (DST-correct) and prepareToSubmit
  // strips `occurrences` from the submit payload, so a browser-zone client
  // preview could DIVERGE from what is stored for cross-zone / DST-boundary
  // submitters. The live preview is now fed by useServerOccurrencesPreview
  // (the previewEventOccurrences query — SAME server pipeline EventCreate
  // uses; see the effect below). This callback keeps ONLY the FORM-FIELD
  // adjustments that were always its other job and are unrelated to the
  // preview data source:
  //   * setRepeatStopsOnBasedOnRepeatingPattern (seeds repeatStopsOn /
  //     repeatDays when the pattern changes), and
  //   * the overnight endAt +1-day fix (the form has no end-DATE field), which
  //     mutates the FORM model so the user sees a corrected end — the SERVER
  //     independently re-normalizes overnight in the resolved zone for the
  //     *_local path (Events::NormalizeWallClockSpan), so this is form UX, not
  //     the source of truth.
  const adjustScheduleFields = useCallback(
    (key, value) => {
      const formReference = formRef.current
      if (!formReference) {
        return
      }

      if (setRepeatStopsOnBasedOnRepeatingPattern(formReference, key, value)) {
        return
      }
      const keyStart = key.split('.')[0]
      const scheduleFields = ['eventType', 'repeatDays', 'repeatStopsOn', 'startAtDate', 'startAtTime', 'endAtTime']
      if (!scheduleFields.includes(keyStart)) {
        return
      }

      const model = formReference.getModel() as Event
      const event = new Event(model)
      // set here only to compute the corrected endAt below; to change form
      // model values use `formReference.change`.
      set(event, key, value)

      const oldEndAt = event.endAt
      event.endAtDate = event.startAtDate
      if (dayjs(event.endAt).isBefore(dayjs(event.startAt))) {
        event.endAt = dayjs(event.endAt).add(1, 'day').toDate()
      }
      if (oldEndAt?.getTime() !== event.endAt?.getTime()) {
        formReference.change('endAt', event.endAt)
      }
    },
    [setRepeatStopsOnBasedOnRepeatingPattern],
  )

  // What the flyer filled in, for the person to check (CVL-177). A change the flyer itself
  // makes keeps its marker; the person's own change clears it.
  const [flyerMarksShown, setFlyerMarksShown] = useState<FlyerMarks | null>(null)
  const applyingFlyer = useRef(false)

  const onChange = useCallback(
    (key, value) => {
      if (!applyingFlyer.current) setFlyerMarksShown(marks => flyerMarksAfterChange(marks, key))
      updateSubmitterOwnsEvent(key, value)

      // FORM-FIELD adjustments only (seeds repeatStopsOn/repeatDays; overnight
      // endAt +1-day). The occurrences preview is server-fed below.
      adjustScheduleFields(key, value)

      // Phase 10: feed venue/community selection into the cross-zone confirm
      // gate. Only a selected venue OR community drives the server-resolved-zone
      // query (no-selection footgun). The date/time shown IN the confirm modal
      // is computed at render from the form model (see `confirmWhenLabel`).
      if (key === 'placeId') {
        setPlaceId(value || null)
      } else if (key === 'primaryCommunityId') {
        setPrimaryCommunityId(value || null)
      } else if (key === 'useTempPlace') {
        setUseTempPlace(Boolean(value))
      } else if (key.split('.')[0] === 'tempPlace') {
        applyTempPlaceChange(key, value, setTempPlaceState, setTempPlaceCity)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adjustScheduleFields, updateSubmitterOwnsEvent],
  )

  const onChangeModel = useCallback((model: Event) => {
    setOccurrencesInput(prev => {
      const next = occurrencesInputFromModel(model)
      return occurrencesPreviewKey(prev) === occurrencesPreviewKey(next) ? prev : next
    })
  }, [])

  const onOrganizerTypeChange = useCallback(
    (type: OrganizerType, selectedOrgId: string | null) => {
      const formReference = formRef.current
      if (!formReference) return

      formReference.change('organizerType', type)
      formReference.change('selectedOrganizationId', selectedOrgId)

      if (type === 'myself') {
        // Pre-fill with current user info and set submitterOwnsEvent
        formReference.change('submitterOwnsEvent', true)
        formReference.change('organizer.name', get(currentUser, 'fullName'))
        formReference.change('organizer.email', get(currentUser, 'email'))
        formReference.change('organizer.phone', '')
      } else if (type === 'organization' && selectedOrgId) {
        // Pre-fill with organization info and clear submitterOwnsEvent
        formReference.change('submitterOwnsEvent', false)
        const org = organizations.find((o) => o.id === selectedOrgId)
        if (org) {
          formReference.change('organizer.name', org.name || '')
          formReference.change('organizer.email', org.contactEmail || '')
          formReference.change('organizer.phone', formatPhoneForForm(org.phone))
        }
      } else if (type === 'other') {
        // Clear for manual entry and mark as not submitter-owned
        formReference.change('submitterOwnsEvent', false)
        formReference.change('organizer.name', '')
        formReference.change('organizer.email', '')
        formReference.change('organizer.phone', '')
      }
    },
    [currentUser, organizations],
  )

  // Fills in what the flyer read found, one field at a time through the form so the
  // cross-zone state above follows, and never over a field the person already changed.
  // What the form held before any flyer, plus what earlier flyers filled in: a later flyer
  // may replace those, but not what the person changed themselves.
  const flyerBaseline = useRef<Event | null>(null)
  const applyFlyerChanges = useCallback(
    (changes: (current: Event, baseline: Event) => FormChange[]): FormChange[] | null => {
      const formReference = formRef.current
      if (!formReference || saveStarted.current) return null
      const baseline = flyerBaseline.current || originalEvent
      const applied = changes(formReference.getModel() as Event, baseline)
      applyingFlyer.current = true
      try {
        applied.forEach(([key, value]) => formReference.change(key, value))
      } finally {
        applyingFlyer.current = false
      }
      if (!applied.length) return applied
      // From the changes themselves: inside a React batch the form's model updates later.
      const filled = cloneDeep(baseline)
      applied.forEach(([key, value]) => set(filled, key, cloneDeep(value)))
      flyerBaseline.current = filled
      return applied
    },
    [originalEvent],
  )
  const onFlyerRead = useCallback(
    (candidate: FlyerCandidate, image: FlyerImage | null) => {
      const applied = applyFlyerChanges((current, baseline) => flyerCandidateToFormChanges(candidate, current, baseline, image))
      if (applied) setFlyerMarksShown(flyerMarks(candidate, applied))
    },
    [applyFlyerChanges],
  )
  const onFlyerNotRead = useCallback(
    (image: FlyerImage | null) => applyFlyerChanges((current, baseline) => flyerImageChanges(image, current, baseline)),
    [applyFlyerChanges],
  )
  const onFlyerReadStarted = useCallback((id: string) => {
    flyerExtractionId.current = id
  }, [])

  // Pre-fill organizer: prefer the user's saved default organization, else auto-default
  // to a single C4B org when one exists.
  const [autoDefaultApplied, setAutoDefaultApplied] = useState(false)
  useEffect(() => {
    if (orgsLoading || autoDefaultApplied || !organizations.length) return
    const userDefaultOrg = defaultOrganizationId && organizations.find((o) => o.id === defaultOrganizationId)
    if (userDefaultOrg) {
      onOrganizerTypeChange('organization', userDefaultOrg.id)
      setAutoDefaultApplied(true)
      return
    }
    const c4bOrgs = organizations.filter((o) => o.hasActiveSubscription)
    if (c4bOrgs.length === 1) {
      onOrganizerTypeChange('organization', c4bOrgs[0].id)
      setAutoDefaultApplied(true)
    }
  }, [orgsLoading, organizations, defaultOrganizationId, autoDefaultApplied, onOrganizerTypeChange])

  if (loading || orgsLoading) return <Loading className="isDark" />

  // Phase 10 (modal date/time): the cross-zone confirm shows the full date +
  // time being saved so the submitter can confirm exactly what they commit.
  // Computed at render — the modal only opens after the user clicks Submit, so
  // the form model is finalized; reuse `eventsSummary` (the same helper the
  // on-form Summary box uses) for correct single / all-day / series wording.
  const confirmModel = formRef.current?.getModel() as Event | undefined
  const confirmWhenLabel = modalProps.isOpen && confirmModel ? eventsSummary(confirmModel) : null

  return (
    <div>
      {socialShareEvent && socialShareEvent.orgId && (
        <SocialShareModal
          eventId={socialShareEvent.id}
          eventTitle={socialShareEvent.title}
          eventImagePublicId={socialShareEvent.imagePublicId}
          organizationId={socialShareEvent.orgId}
          onClose={handleSocialShareClose}
        />
      )}

      <CrossZoneConfirmModal {...modalProps} whenLabel={confirmWhenLabel} />

      <h1 className="content-form-heading">Add Event</h1>

      <FlyerStartPanel
        primaryCommunityId={primaryCommunityId}
        stopped={flyerStopped}
        onReadStarted={onFlyerReadStarted}
        onRead={onFlyerRead}
        onNotRead={onFlyerNotRead}
      />

      <AutoForm
        disabled={loading}
        ref={formRef}
        schema={EventFormSchema}
        model={originalEvent}
        className="form content-entry-form event-form"
        onChange={onChange}
        onChangeModel={onChangeModel}
        onSubmit={onSubmit}
        onValidate={onValidate}
      >
        <ResolvedZoneContext.Provider value={resolvedZone}>
          <FlyerPrefillContext.Provider value={flyerMarksShown}>
            <EventFields
              onImageUploadStateChange={onImageUploadStateChange}
              originalEvent={originalEvent}
              organizations={organizations}
              onOrganizerTypeChange={onOrganizerTypeChange}
              userDefaultOrganizationId={defaultOrganizationId}
              saveAsDefault={saveAsDefault}
              onSaveAsDefaultChange={setSaveAsDefault}
            />
          </FlyerPrefillContext.Provider>
        </ResolvedZoneContext.Provider>

        <fieldset className="submit-row">
          <UniformsButton disabled={isSubmitDisabled || mutationLoading} shouldShowLoadingSpinner={mutationLoading} text="Submit Event" />
          {checkingZone ? (
            <span className="submit-row-note" role="status">
              Checking time zone…
            </span>
          ) : null}
        </fieldset>
      </AutoForm>
    </div>
  )
}

export default EventNew
